import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { Badge, TableSkeleton } from "../../../components/ui";
import { getPaymentTimeline } from "../../../services/monitoring";

export const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

export const formatAmount = (amount, currency) => {
  if (amount === undefined || amount === null || amount === "") return "—";
  const code = (currency || "").toUpperCase();
  const cfa = code === "XOF" || code === "XAF";
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: code || "USD",
      maximumFractionDigits: cfa ? 0 : 2,
    }).format(Number(amount));
  } catch {
    return `${amount} ${code}`;
  }
};

export const KIND_LABELS = {
  subscription: "Subscription",
  subscription_renewal: "Renewal",
  exclusive: "Exclusive content",
  donation: "Donation",
};

export const LEVELS = {
  error: { label: "Error", color: "red", icon: "ri-error-warning-line" },
  warn: { label: "Warning", color: "amber", icon: "ri-alert-line" },
  info: { label: "Info", color: "blue", icon: "ri-information-line" },
  debug: { label: "Debug", color: "slate", icon: "ri-bug-line" },
};

export const LevelBadge = ({ level }) => {
  const { t } = useTranslation();
  const known = LEVELS[level] || LEVELS.info;
  return (
    <Badge color={known.color} dot>
      {t(known.label)}
    </Badge>
  );
};

/** Readable events of the log (fallback: the raw event name). */
export const EVENT_LABELS = {
  "payment.started": "Payment opened",
  "payment.succeeded": "Payment received",
  "payment.failed": "Payment failed",
  "payment.abandoned": "Abandoned (not validated in 24 h)",
  "payment.delivery_failed": "Paid but not delivered",
  "payment.past_due": "Renewal declined",
  "payment.action_required": "Bank validation required",
  "payment.recovered": "Unpaid settled",
  "payment.reviewed": "Follow-up updated",
  "cinetpay.notification": "CinetPay notification",
  "paystack.webhook": "Paystack webhook",
  "paystack.webhook_invalid": "Paystack webhook rejected (signature)",
  "paystack.init_failed": "Paystack refused to open the payment",
  "paystack.check_failed": "Paystack check impossible",
  "paystack.lookup": "Looked up from the back office",
  "pawapay.callback": "pawaPay callback",
  "pawapay.init_failed": "pawaPay refused to open the payment",
  "pawapay.check_failed": "pawaPay check impossible",
  "pawapay.lookup": "Looked up from the back office",
  "mobile.rates_changed": "Conversion rates changed",
  "payment.amount_mismatch": "Amount paid lower than the price",
  "mobile.unknown_transaction": "Unknown transaction",
  "mobile.provider_suspended": "Provider suspended",
  "mobile.provider_resumed": "Provider reactivated",
  "mobile.priority_changed": "Routing order changed",
  "cinetpay.init_failed": "CinetPay refused to open the payment",
  "cinetpay.check_failed": "CinetPay check impossible",
  "cinetpay.unknown_transaction": "Unknown transaction",
  "cinetpay.lookup": "Looked up from the back office",
  "admin.recheck": "Rechecked from the back office",
  "admin.remind": "Reminder sent from the back office",
  "admin.retry_charge": "New charge attempt",
  "admin.stripe_sync": "Stripe synchronisation",
  "admin.action": "Admin action",
  "admin.action_failed": "Admin action refused or failed",
  "admin.login": "Back-office login",
  "admin.login_failed": "Back-office login refused",
  "maintenance.run": "Maintenance task run",
  "maintenance.failed": "Maintenance task failed",
  "stripe.webhook_invalid": "Stripe webhook rejected (signature)",
  "stripe.webhook_failed": "Stripe webhook processing failed",
};

/** Names of the context fields recorded by the API. */
const CONTEXT_LABELS = {
  kind: "Type",
  type: "Type",
  amount: "Amount",
  currency: "Currency",
  provider: "Provider",
  operator: "Operator",
  operatorMessage: "Operator message",
  status: "Status",
  code: "Code",
  message: "Message",
  signatureCheck: "Signature",
  site: "Site",
  reason: "Reason",
  declineCode: "Decline code",
  attempt: "Attempt",
  plan: "Plan",
  graceUntil: "Grace until",
  delivered: "Delivered",
  result: "Result",
  live: "Live",
  by: "By",
  admin: "Admin",
  livemode: "Live mode",
  stack: "Trace",
  ip: "IP",
};

/** Details of a log line (context), as key: value pairs. */
export const ContextList = ({ context }) => {
  const { t } = useTranslation();
  if (!context || typeof context !== "object") return null;
  const entries = Object.entries(context).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return null;
  return (
    <dl className="logs-context">
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{CONTEXT_LABELS[key] ? t(CONTEXT_LABELS[key]) : key}</dt>
          <dd>{typeof value === "object" ? <pre>{JSON.stringify(value, null, 2)}</pre> : String(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

/** Everything that happened to a payment, in order. */
export const Timeline = ({ events }) => {
  const { t } = useTranslation();
  if (!events?.length) return <p className="ui-hint">{t("Nothing recorded for this payment yet.")}</p>;
  return (
    <ol className="logs-timeline">
      {events.map((event) => (
        <li key={event.id} data-level={event.level}>
          <div className="logs-timeline-head">
            <strong>{t(EVENT_LABELS[event.event] || event.event)}</strong>
            <span>{formatDate(event.at)}</span>
          </div>
          {event.message && <p>{event.message}</p>}
          <ContextList context={event.context} />
        </li>
      ))}
    </ol>
  );
};

/** Modal with a payment and its history. */
export const PaymentDetails = ({ paymentId, onClose, children }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (paymentId) getPaymentTimeline(paymentId).then(setData);
  }, [paymentId]);
  const p = data?.payment;
  return (
    <Modal show={Boolean(paymentId)} onHide={onClose} centered size="lg" dialogClassName="ui-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t("Payment history")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!data ? (
          <TableSkeleton rows={3} />
        ) : (
          <>
            <div className="logs-payment-head">
              <div>
                <span className="ui-label">{t("Customer")}</span>
                <strong>{p.customer?.name}</strong>
                <span className="ui-hint">{p.customer?.email || p.phone}</span>
              </div>
              <div>
                <span className="ui-label">{t("Amount")}</span>
                <strong>{formatAmount(p.amount, p.currency)}</strong>
                <span className="ui-hint">{t(KIND_LABELS[p.kind] || p.kind)}</span>
              </div>
              <div>
                <span className="ui-label">{t("Transaction")}</span>
                <code>{p.transactionId}</code>
                <span className="ui-hint">{p.operator || "—"}</span>
              </div>
            </div>
            {children && children(p)}
            <h3 className="logs-subtitle">{t("History")}</h3>
            <Timeline events={data.events} />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
